import { Callout, Pill } from "@clipboard-health/ui-components";
import { isDefined } from "@clipboard-health/util-ts";
import { Box, Divider, Stack } from "@mui/material";
import { isCbhApiAxiosError } from "@src/appV2/Errors/errorUtils";
import { APP_V2_APP_EVENTS, logError } from "@src/appV2/lib/analytics";
import { ChatActionCard } from "@src/appV2/redesign/Chat/ChatActionCard";
import { Button } from "@src/appV2/redesign/components/Button";
import { LoadingButton } from "@src/appV2/redesign/components/LoadingButton";
import { useBookVisit } from "@src/appV2/redesign/InHome/BookingBottomSheet/useBookVisit";
import { VisitStatuses } from "@src/appV2/redesign/InHome/constants";
import { BOOKED_VISIT_DETAILS_FULL_PATH } from "@src/appV2/redesign/InHome/paths";
import { useGetVisit } from "@src/appV2/redesign/InHome/useGetVisitData";
import { useGetWorkplaceProfile } from "@src/appV2/redesign/Workplace/useGetWorkplaceProfile";
import { useDefinedWorker } from "@src/appV2/Worker/useDefinedWorker";
import { generatePath, useHistory } from "react-router-dom";

import { ChatWidgetWrapper } from "../../redesign/Chat/ChatWidgetWrapper";
import { InHomeVisitCardSkeleton } from "../../redesign/InHome/Card/Skeleton";
import { type VisitInviteMessageProps } from "../type";
import { parseVisitInviteChatMessage } from "../utils";

function VisitInvitePill(props: { isOpen: boolean; isBookedByCurrentWorker: boolean }) {
  const { isOpen, isBookedByCurrentWorker } = props;
  if (isBookedByCurrentWorker) {
    return (
      <Pill
        variant="outlined"
        size="small"
        label="Invite accepted"
        backgroundColor={(theme) => theme.palette.intent?.success.background}
        color={(theme) => theme.palette.intent?.success.text}
        outlineColor={(theme) => theme.palette.intent?.success.border}
      />
    );
  }

  if (!isOpen) {
    return (
      <Pill
        variant="outlined"
        size="small"
        label="Invite expired"
        backgroundColor={(theme) => theme.palette.background.quaternary}
        color={(theme) => theme.palette.text.primary}
        outlineColor={(theme) => theme.palette.border?.primary}
      />
    );
  }

  return (
    <Pill
      variant="outlined"
      size="small"
      label="Visit invite"
      backgroundColor={(theme) => theme.palette.background.quaternary}
      color={(theme) => theme.palette.intent?.warning.text}
      outlineColor={(theme) => theme.palette.intent?.warning.border}
    />
  );
}

export function VisitInviteRequestMessage(props: VisitInviteMessageProps) {
  const { workplaceId } = props;
  const { visitId, caseId } = parseVisitInviteChatMessage(props);
  const {
    isLoading: isVisitLoading,
    isError: isVisitError,
    data,
  } = useGetVisit({ visitId, caseId, geoLocation: { latitude: 0, longitude: 0 } });
  const worker = useDefinedWorker();
  const history = useHistory();
  const visit = data?.data;
  const caseData = data?.included?.find((item) => item.type === "case");
  const patientData = caseData?.attributes?.patient;
  const { bookedWorkerId, workerReq, status, type } = visit?.attributes ?? {};

  const {
    data: workplaceDetails,
    isLoading: isWorkplaceLoading,
    isError: isWorkplaceError,
  } = useGetWorkplaceProfile({ workplaceId });
  const workplaceName = workplaceDetails?.name;

  const {
    mutateAsync: bookVisit,
    isLoading: isBookingVisitLoading,
    error: bookVisitError,
    isError: isBookingVisitError,
  } = useBookVisit(visitId ?? "");

  const isError = isVisitError || isWorkplaceError;
  const isLoading = isVisitLoading || isWorkplaceLoading;
  if (isError || !isDefined(visitId) || !isDefined(caseId)) {
    logError(APP_V2_APP_EVENTS.VISIT_INVITE_REQUEST_MESSAGE_RENDER_FAILURE, {
      error: new Error("Failed to render visit invite request message"),
      metadata: {
        visitId,
        caseId,
        isVisitError,
        isWorkplaceError,
      },
    });
    return null;
  }

  if (isLoading) {
    return (
      <ChatWidgetWrapper>
        <InHomeVisitCardSkeleton loadingLabel="Loading visit invite" />
      </ChatWidgetWrapper>
    );
  }

  const isOpen = status === VisitStatuses.OPEN;
  const isBookedByCurrentWorker = bookedWorkerId === worker.userId;
  const isDisabled = !(isOpen || isBookedByCurrentWorker);

  const bookedVisitPath = generatePath(BOOKED_VISIT_DETAILS_FULL_PATH, {
    caseId,
    visitId,
  });

  const inviteMessage = `${workplaceName} invited you to a ${type} ${workerReq} visit for ${patientData?.attributes.externalPatientIdentifier}`;

  const bookingErrorMessage = isCbhApiAxiosError(bookVisitError)
    ? bookVisitError.response.data.errors[0].detail
    : "Unknown error while booking visit";

  return (
    <ChatWidgetWrapper>
      <Box sx={{ position: "relative" }}>
        <ChatActionCard
          message={inviteMessage}
          pill={
            <VisitInvitePill isOpen={isOpen} isBookedByCurrentWorker={isBookedByCurrentWorker} />
          }
        >
          <Stack direction="column" width="100%" alignItems="flex-end" spacing={7}>
            {isBookingVisitError && (
              <Callout
                iconType="remove"
                title={bookingErrorMessage}
                sx={(theme) => ({
                  backgroundColor: theme.palette.intent?.error.background,
                  color: theme.palette.intent?.error.text,
                })}
              />
            )}
            <Divider sx={{ width: "100%" }} />
            {isOpen && (
              <LoadingButton
                fullWidth
                variant="contained"
                size="small"
                startIconType="checkmark"
                disabled={isDisabled}
                isLoading={isBookingVisitLoading}
                onClick={async () => {
                  await bookVisit({
                    data: {
                      type: "visit",
                      attributes: { bookedWorkerId: worker.userId },
                    },
                  });
                }}
              >
                Accept
              </LoadingButton>
            )}
            {isBookedByCurrentWorker && (
              <Button
                fullWidth
                variant="contained"
                size="small"
                onClick={() => {
                  history.push(bookedVisitPath);
                }}
              >
                Go to Visit Details
              </Button>
            )}
          </Stack>
        </ChatActionCard>
        {isDisabled && (
          <Box
            sx={{
              backgroundColor: "rgba(255, 255, 255, 0.5)",
              position: "absolute",
              top: 0,
              left: 0,
              width: "100%",
              height: "100%",
              zIndex: 2,
            }}
          />
        )}
      </Box>
    </ChatWidgetWrapper>
  );
}
